import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PAYMENT } from "@/constants/testIds";
import { FollowKiddee } from "@/components/FollowKiddee";
import { STORE } from "@/config/sections";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking" });
  const tries = useRef(0);

  useEffect(() => {
    const id = params.get("session_id");
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${id}`);
        if (data.payment_status === "paid") {
          clear();
          return setState({ status: "paid", orderId: data.order_id });
        }
        if (data.status === "expired" || tries.current++ > 15) return setState({ status: "failed" });
        timer = setTimeout(poll, 2000);
      } catch {
        setState({ status: "failed" });
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Icon = state.status === "paid" ? CheckCircle2 : state.status === "failed" ? XCircle : Loader2;
  return (
    <main data-testid={PAYMENT.success} className="pt-36 pb-10 max-w-xl mx-auto px-6 text-center min-h-[60vh]">
      <Icon size={56} className={`mx-auto ${state.status === "paid" ? "text-[#4E7B68]" : state.status === "failed" ? "text-[#E11D48]" : "animate-spin text-stone-400"}`} />
      <h1 data-testid={PAYMENT.status} className="font-heading mt-6 text-3xl sm:text-4xl font-extrabold">
        {state.status === "paid" ? "Order confirmed. Kiddee approves." : state.status === "failed" ? "We couldn't confirm your payment." : "Confirming your payment…"}
      </h1>
      {state.orderId && <p className="mt-4 text-stone-600">Order number <span data-testid={PAYMENT.orderId} className="font-semibold">{state.orderId}</span>. Most orders ship in 1–2 business days.</p>}
      {state.status === "failed" && <p className="mt-4 text-stone-600">If you were charged, email us at <a href={`mailto:${STORE.email}`} className="underline text-[#E06D44]">{STORE.email}</a> with your receipt and we'll sort it out.</p>}
      <div className="mt-10 flex justify-center gap-3">
        <Link to="/track" className="px-6 py-3 rounded-full border border-[#1C1917] hover:bg-[#1C1917] hover:text-white transition-colors">Track an order</Link>
        <Link to="/shop" className="px-6 py-3 rounded-full bg-[#E06D44] text-white hover:bg-[#C95B33] transition-colors">Keep shopping</Link>
      </div>
      {state.status === "paid" && <FollowKiddee />}
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main data-testid={PAYMENT.cancel} className="pt-36 pb-10 max-w-xl mx-auto px-6 text-center min-h-[60vh]">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold">Checkout cancelled.</h1>
      <p className="mt-4 text-stone-600">No charge was made. Your cart is still waiting for you.</p>
      <Link to="/cart" className="mt-10 inline-block px-7 py-3.5 rounded-full bg-[#1C1917] text-white hover:bg-[#E06D44] transition-colors">Back to cart</Link>
    </main>
  );
}
